import * as SentryReact from '@sentry/react';
import parsePhoneNumber, {
  PhoneNumber,
  CountryCallingCode,
  NationalNumber,
  CountryCode,
  isValidNumberForRegion,
} from 'libphonenumber-js';

import { countryCallingCodes } from './countryCallingCodes';

import useStore from '../hooks/useStore';
import {
  URUGUAY_TOLL_FREE_PREFIX,
  URUGUAY_TOLL_FREE_DIALING_PREFIX,
} from '../constants';

// this function parses a number and returns the parsed number and if anything goes wrong,
// returns undefined
export function safePhoneNumberParser(
  valueToParse: string,
): PhoneNumber | undefined {
  let parsedPhoneNumber;
  try {
    parsedPhoneNumber = parsePhoneNumber(valueToParse);
  } catch (e) {
    SentryReact.addBreadcrumb({
      category: 'number',
      message: 'Error parsing number',
      data: { number: valueToParse },
    });
    SentryReact.captureException(e);
  }
  return parsedPhoneNumber;
}

function removeDuplicatedCountryCode(value: string): string {
  const { defaultCountryCode } = useStore.getState();
  const countryCodePattern = `^(?:${defaultCountryCode})+`;
  const countryCodeRegex = new RegExp(countryCodePattern);

  // Replace the duplicated country code with a single instance
  return value.replace(countryCodeRegex, `+${defaultCountryCode}`);
}

function hasDuplicatedCountryCode(value: string): boolean {
  const { defaultCountryCode } = useStore.getState();
  const countryCodePattern = `^(?:${defaultCountryCode})+`;
  const countryCodeRegex = new RegExp(countryCodePattern);

  // Check if the country code is duplicated
  return countryCodeRegex.test(value);
}

function checkNumberAgainstDefaultCountryCode(value: string): boolean {
  const { defaultCountryCode } = useStore.getState();
  const countriesWithDefaultCountryCode = countryCallingCodes.filter(
    entry => entry.callingCode === `+${defaultCountryCode}`,
  );
  return countriesWithDefaultCountryCode.some(matchedCountry =>
    isValidNumberForRegion(value, matchedCountry.country as CountryCode),
  );
}

export function stripNumberFromExtraCharacters(value: string): string {
  // Normalize input - keep the leading + but remove: all whitespaces, parentheses, dashes, special characters.
  return value.replace(/(?!^\+)[\s-()]/g, '');
}

export function preparePhoneNumberForParsing(value: string): string {
  const normalizedValue = stripNumberFromExtraCharacters(value);
  const { defaultCountryCode } = useStore.getState();

  // In this case the number is most probably correctly formatted
  if (normalizedValue.startsWith('+')) {
    return normalizedValue;
  }

  // Replace 00 with '+' as '00' is the direct alternative for '+'
  if (normalizedValue.startsWith('00')) {
    return normalizedValue.replace('00', '+');
  }

  // Handle cases where the number starts with a single '0' (local format)
  // and replace it with the default country code
  if (normalizedValue.startsWith('0')) {
    return `+${defaultCountryCode}${normalizedValue.slice(1)}`;
  }

  // Handle cases where a number is valid for the default country code (from settings)
  // but has been pasted without the country code, as a national number.
  // Example: International format: +1 8669049666; Pasted as: 8669049666
  if (checkNumberAgainstDefaultCountryCode(normalizedValue)) {
    if (hasDuplicatedCountryCode(normalizedValue)) {
      return removeDuplicatedCountryCode(normalizedValue);
    }
    return `+${defaultCountryCode}${normalizedValue}`;
  }

  // In this case we want to avoid situations when user is dialing a national
  // number and his default country code set in Dialer is for the same country
  // as the number that user is calling
  // In such case we remove the duplicate country code number.
  // Example: default country code is +44 and user calls 44 123123123,
  // this would give +44 44123123123 if we didn't remove the duplicate
  // country code.
  if (hasDuplicatedCountryCode(normalizedValue)) {
    return removeDuplicatedCountryCode(normalizedValue);
  }

  // If the default country code is not already contained in the number, then
  // add it to number and return
  return `+${defaultCountryCode}${normalizedValue}`;
}

// Only returns true for valid international numbers.
export function validatePhoneNumber(value: string): boolean {
  const valueToParse = preparePhoneNumberForParsing(value);
  const phoneNumber = safePhoneNumberParser(valueToParse);

  if (phoneNumber) {
    return phoneNumber.isValid();
  }
  return false;
}

export function formatPhoneNumber(value: string): string | null {
  if (!value) return null;
  const valueToParse = preparePhoneNumberForParsing(value);
  const phoneNumber = safePhoneNumberParser(valueToParse);
  if (!phoneNumber) {
    const { defaultCountryCode } = useStore.getState();
    if (value.startsWith('+')) {
      return null;
    }
    return formatPhoneNumber(`+${defaultCountryCode}${value}`);
  }
  let formatted;
  if (valueToParse.startsWith('+')) {
    formatted = phoneNumber.formatInternational();
  } else {
    formatted = phoneNumber.formatNational();
  }
  if (formatted.includes('ext.')) {
    formatted = formatted.slice(0, formatted.indexOf('ext.')).trim();
  }
  return formatted;
}

/**
 * Extracts the digits of an international phone number without the country code
 * e.g. +1 213 373 4253 => 2133734253
 */
export function deriveDigitsFromPhoneNumber(
  value: string,
): NationalNumber | null {
  const valueToParse = preparePhoneNumberForParsing(value);
  if (validatePhoneNumber(valueToParse)) {
    const phoneNumber = safePhoneNumberParser(valueToParse);
    return (phoneNumber && phoneNumber.nationalNumber) || null;
  }

  return null;
}

export function deriveCountryCodeFromPhoneNumber(
  value: string,
): CountryCallingCode | null {
  const valueToParse = preparePhoneNumberForParsing(value);
  if (validatePhoneNumber(valueToParse)) {
    const phoneNumber = safePhoneNumberParser(valueToParse);
    return (phoneNumber && phoneNumber.countryCallingCode) || null;
  }

  return null;
}

export function isInternationalNumber(
  countryCode: string | undefined,
): boolean {
  const localCountryCodes = ['US', 'CA', 'VI'];
  return localCountryCodes.includes(countryCode || '') !== true;
}

export function fetchAreaCodeFromPhoneNumber(nationalNumber: string): string {
  const areaCode = nationalNumber.toString().substring(0, 3);
  return areaCode;
}

export function normalizePhoneNumber(number: string): string {
  const digitsOnly = number.replace(/\D/g, ''); // Extract only digits
  return `+${digitsOnly}`;
}

// prepare from number according to Twilio's requirements
export function prepareFromNumber(
  convertUruguayTollFreeNumberEnabled: boolean,
  from: string,
): string {
  if (
    from.startsWith(URUGUAY_TOLL_FREE_PREFIX) &&
    convertUruguayTollFreeNumberEnabled
  ) {
    return from.replace(
      URUGUAY_TOLL_FREE_PREFIX,
      URUGUAY_TOLL_FREE_DIALING_PREFIX,
    );
  }
  return from;
}
